import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Timer as TimerIcon } from "@mui/icons-material";
import io from "socket.io-client";
import { MYM_GAME_LABELS } from "./useMymTitlesConfig";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getAdminToken = () =>
  localStorage.getItem("admin_token") || localStorage.getItem("auth_token");

const ForceAutoOffSection = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [hours, setHours] = useState(2);
  const [forceLive, setForceLive] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchConfig = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`${API_BASE}/api/settings/mym-force-auto-off`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEnabled(!!data.enabled);
        setHours(parseInt(data.hours, 10) || 2);
        setForceLive(data.forceLive || null);
      }
    } catch (err) {
      console.error("Failed to fetch force auto-off:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socket.on("mym.force_open", () => fetchConfig({ silent: true }));
    socket.on("mym.force_clear", () => fetchConfig({ silent: true }));
    return () => {
      socket.disconnect();
    };
  }, [fetchConfig]);

  const save = async (nextEnabled, nextHours) => {
    const clamped = Math.min(24, Math.max(1, parseInt(nextHours, 10) || 1));
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-force-auto-off`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({ enabled: nextEnabled, hours: clamped }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to save auto-off");
      }
      setEnabled(!!data.enabled);
      setHours(data.hours);
      setForceLive(data.forceLive || null);
      setMessage(
        data.enabled
          ? `Auto off enabled — force mode stops after ${data.hours} hour${data.hours === 1 ? "" : "s"}`
          : "Auto off disabled"
      );
    } catch (err) {
      setMessage(err.message || "Failed to save auto-off");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={28} sx={{ color: "#ff4081" }} />
      </Box>
    );
  }

  const liveLabel = forceLive?.game_id
    ? MYM_GAME_LABELS[forceLive.game_id] || forceLive.game_id
    : null;
  const autoOffAtLabel =
    forceLive?.auto_off_at && liveLabel
      ? new Date(forceLive.auto_off_at).toLocaleString()
      : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        mt: compact ? 2 : 3,
        backgroundColor: "#1E1E1E",
        border: "1px solid #333",
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <TimerIcon sx={{ color: "#ff4081" }} />
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 600,
            fontSize: compact ? "0.95rem" : "1.1rem",
          }}
        >
          Auto off Force mode
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#888", mb: 2 }}>
        Applies to every game. When enabled, whichever game is forced turns off
        automatically after the set hours. Forcing a game starts the timer;
        changing hours while force is on restarts it from now.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={saving}
            onChange={(e) => save(e.target.checked, hours)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff4081" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#ff4081",
              },
            }}
          />
        }
        label={
          <Typography sx={{ color: "#fff" }}>
            {enabled ? "Auto off is ON" : "Auto off is OFF"}
          </Typography>
        }
      />

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
        <TextField
          type="number"
          label="Hours"
          value={hours}
          disabled={!enabled || saving}
          onChange={(e) => setHours(e.target.value)}
          onBlur={() => {
            const next = Math.min(24, Math.max(1, parseInt(hours, 10) || 1));
            setHours(next);
          }}
          inputProps={{ min: 1, max: 24, step: 1 }}
          helperText="1–24 hours from start"
          sx={{
            width: 160,
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              "& fieldset": { borderColor: "#444" },
              "&:hover fieldset": { borderColor: "#666" },
              "&.Mui-focused fieldset": { borderColor: "#ff4081" },
            },
            "& .MuiInputLabel-root": { color: "#aaa" },
            "& .MuiFormHelperText-root": { color: "#666" },
          }}
        />
        <Button
          variant="outlined"
          disabled={!enabled || saving}
          onClick={() => save(true, hours)}
          sx={{
            mt: 0.5,
            color: "#ff4081",
            borderColor: "rgba(255,64,129,0.5)",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { borderColor: "#ff4081", bgcolor: "rgba(255,64,129,0.08)" },
          }}
        >
          {saving ? "Saving..." : "Save hours"}
        </Button>
      </Box>

      {autoOffAtLabel && (
        <Typography sx={{ color: "#ff80ab", fontSize: "0.85rem", mt: 1.5 }}>
          {liveLabel} is forced — auto off at {autoOffAtLabel}
        </Typography>
      )}
      {message && (
        <Typography sx={{ color: "#aaa", fontSize: "0.8rem", mt: 1 }}>
          {message}
        </Typography>
      )}
    </Paper>
  );
};

export default ForceAutoOffSection;
